<template>
  <div class="fill-height">
    <v-navigation-drawer
      :value="drawer"
      app
      :color="drawerColor"
      :dark="isDrawerDark"
      @input="(show) => $store.dispatch('setDrawer', show)"
    >
      <template v-slot:prepend>
        <v-row>
          <v-col
            class="d-flex flex-column justify-center align-center"
            cols="12"
          >
            <v-img
              contain
              height="150"
              max-width="85%"
              src="https://noormarkunvpk.fi/wp-content/uploads/2017/03/logo.png"
            />
            <h1
              :class="drawerTitleClass"
              style="font-size: x-large"
            >
              Noormarkun VPK
            </h1>
          </v-col>
        </v-row>
        <v-divider></v-divider>
      </template>

      <v-list dense nav>
        <template v-for="(routes, category) in groupedRoutes">
          <div :key="category" v-if="routes.length > 0">
            <v-subheader
              class="text-uppercase font-weight-black text-subtitle-1"
              :class="drawerSubheaderClass"
              :style="
                !modernTheme ? 'background-color: rgba(0, 0, 0, 0.2)' : ''
              "
              >{{ category }}</v-subheader
            >
            <v-list-item
              v-for="route in routes"
              :key="route.name"
              :to="route.path"
              link
              :color="modernTheme ? 'primary' : undefined"
              class="mb-1 rounded-lg"
            >
              <v-list-item-icon v-if="route.icon">
                <v-icon>{{ route.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ route.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </div>
        </template>
      </v-list>
      <v-divider />

      <template v-slot:append>
        <v-list dense nav>
          <v-list-item
            link
            to="/asetukset"
            :color="modernTheme ? 'primary' : undefined"
            class="mb-1 rounded-lg"
          >
            <v-list-item-icon>
              <v-icon>mdi-cog</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Asetukset</v-list-item-title>
            </v-list-item-content>
          </v-list-item>

          <v-list-item
            link
            @click="$logout"
            :color="modernTheme ? 'primary' : undefined"
            class="mb-1 rounded-lg"
          >
            <v-list-item-icon>
              <v-icon>mdi mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Kirjaudu ulos</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider />

          <v-list-item
            class="refresh-list-item mb-1 rounded-lg"
            link
            @click="reload"
            :color="modernTheme ? 'primary' : undefined"
          >
            <v-list-item-icon>
              <v-icon>mdi mdi-refresh</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-subtitle>
                <div class="d-flex align-center justify-lg-space-between">
                  <span :class="[version !== latestVersion && 'info--text']">{{
                    versionShort || "##############"
                  }}</span>
                </div>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </template>
    </v-navigation-drawer>
    <v-app-bar
      height="60"
      app
      :color="appBarColor"
      :elevation="modernTheme ? 1 : 4"
      class="safe-area-app-bar"
    >
      <div
        class="d-flex align-center"
        style="width: 100%; height: 100%"
      >
          <v-app-bar-nav-icon
            v-if="!prev"
            :color="appBarContentColor"
            @click.stop="$store.dispatch('setDrawer', !drawer)"
          ></v-app-bar-nav-icon>
          <v-btn
            v-if="prev"
            :color="appBarContentColor"
            icon
            @click="
              () => {
                if (this.forceFallback) {
                  this.$router.push(this.fallback);
                  return;
                }
                hasHistory()
                  ? this.$router.go(-1)
                  : this.$router.push(this.fallback);
              }
            "
          >
            <v-icon>mdi mdi-arrow-left</v-icon>
          </v-btn>
          <responsive-toolbar-title
            :class="[
              appBarTitleClass,
              title && title.length > 10
                ? 'text-subtitle-1 font-weight-bold'
                : '',
            ]"
            class="mr-2"
          >
            {{ title }}
          </responsive-toolbar-title>
          <div class="d-flex align-center flex-shrink-0">
            <slot name="app-bar-buttons"> </slot>
            <v-menu offset-y min-width="300" max-height="90vh" max-width="95vw">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  icon
                  :color="appBarContentColor"
                  class="d-flex align-center"
                  :loading="loadingNotifications"
                >
                  <v-icon>mdi mdi-bell</v-icon>
                  <v-badge color="red" overlap v-if="unreadNotifications > 0">
                    <template v-slot:badge>
                      {{ String(unreadNotifications) }}
                    </template>
                  </v-badge>
                </v-btn>
              </template>
              <v-list>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title>
                      <h3>Ilmoitukset</h3>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <div class="d-flex">
                      <v-btn
                        icon
                        @click="askForNotifications"
                        title="Tilaa ilmoitukset"
                      >
                        <v-icon>mdi-bell-plus</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        @click="clearAllNotifications"
                        v-if="notifications.length > 0"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item
                  v-if="loadingNotifications || notifications.length === 0"
                >
                  <v-list-item-content>
                    <v-list-item-title class="text-wrap">
                      <h4 v-if="loadingNotifications">Ladataan...</h4>
                      <h4 v-else-if="notifications.length === 0">
                        Ei ilmoituksia
                      </h4>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <template v-for="notification in paginatedNotifications">
                  <v-divider :key="`${notification.id}_divider`" />
                  <v-list-item
                    :key="notification.id"
                    :class="{ 'notification-unread': !notification.read }"
                    link
                    @click="navigateToRoute(notification.route, notification)"
                  >
                    <v-list-item-content>
                      <v-list-item-title class="text-wrap">
                        <span>{{ notification.title }}</span>
                      </v-list-item-title>
                      <v-list-item-subtitle class="mt-3">
                        <div
                          class="text-pre-wrap"
                          style="overflow-wrap: break-word"
                          v-html="notification.description"
                        ></div>
                        <small>{{ formatDate(notification.createdAt) }}</small>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn icon @click.stop="markAsRead(notification)">
                        <v-icon>{{
                          notification.read
                            ? "mdi-check-circle-outline"
                            : "mdi-bell-ring-outline"
                        }}</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                </template>
                <!-- Pagination Controls -->
                <v-divider />
                <v-list-item>
                  <v-list-item-content>
                    <v-row>
                      <v-col cols="4">
                        <v-btn
                          outlined
                          small
                          :disabled="currentPage === 1"
                          @click.stop="previousPage"
                        >
                          <v-icon>mdi mdi-chevron-left</v-icon>
                        </v-btn>
                      </v-col>
                      <v-col cols="4">
                        <v-row no-gutters justify="center">
                          <span>Sivu {{ currentPage }} / {{ totalPages }}</span>
                        </v-row>
                      </v-col>
                      <v-col cols="4">
                        <v-row no-gutters justify="end">
                          <v-btn
                            outlined
                            small
                            :disabled="currentPage === totalPages"
                            @click.stop="nextPage"
                            class="ml-2"
                          >
                            <v-icon>mdi mdi-chevron-right</v-icon>
                          </v-btn>
                        </v-row>
                      </v-col>
                    </v-row>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
      </div>
    </v-app-bar>

    <slot name="right-drawer"></slot>

    <v-main class="fill-height safe-area-main">
      <v-container
        :class="[$vuetify.breakpoint.smAndDown && 'pa-0']"
        :fluid="fluid"
        class="fill-height"
      >
        <v-col class="fill-height pt-5" cols="12">
          <v-alert v-if="$isMissingUserRole" outlined type="error"
            >Sinulla ei ole oikeuksia nähdä sivua. Kirjaudu ulos ja ole
            yhteydessä Joni Rautiaiseen jonirautiainen@gmail.com
          </v-alert>
          <slot></slot>
        </v-col>
      </v-container>
    </v-main>
  </div>
</template>

<script>
import { routes } from "@/router";
import api from "@/api";
import dayjs from "dayjs";
import { orderBy } from "lodash";
import app, { getToken } from "@/plugins/firebase.app";
import { getMessaging } from "firebase/messaging";
import ResponsiveToolbarTitle from "@/components/ResponsiveToolbarTitle.vue";

export default {
  name: "BasicPage",
  components: { ResponsiveToolbarTitle },
  props: {
    title: String,
    prev: Boolean,
    fluid: {
      type: Boolean,
      default: true,
    },
    fallback: Object,
    forceFallback: {
      type: Boolean,
      default: false,
    },
  },
  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.reloadNotifications);
    if (this.reloadNotificationsInterval) {
      clearInterval(this.reloadNotificationsInterval);
    }
  },
  mounted() {
    if (this.$isMissingUserRole) {
      this.$toast(
        "Ei oikeuksia. Ota yhteyttä Joni Rautiaiseen. Sinut kirjataan ulos 5 sekunnin päästä."
      );
      setTimeout(() => {
        this.$logout();
      }, 5000);
      return;
    } else {
      this.reloadNotifications();
      document.addEventListener("visibilitychange", this.reloadNotifications);
      this.reloadNotificationsInterval = setInterval(() => {
        this.reloadNotifications();
      }, 60000 * 10);
    }
  },
  data() {
    return {
      notifications: [],
      loadingNotifications: false,
      currentPage: 1,
      pageSize: 5, // Number of notifications per page
      reloadNotificationsInterval: null,
    };
  },
  methods: {
    askForNotifications() {
      const messaging = getMessaging(app);
      (async () => {
        console.log("Notification.permission =", Notification.permission);
        localStorage.removeItem("notificationPermissionDenied");
        try {
          const permission = await Notification.requestPermission();
          if (permission !== "granted") {
            throw Error("Unable to get permission to notify.");
          }

          const serviceWorkerRegistration =
            await navigator.serviceWorker.ready;
          const token = await getToken(messaging, {
            vapidKey:
              "BCZ3S_HG6b-bUNUJkxygFPTu_UDHfthcEcFmCODFxCTVNNVNRbZtRhYWcNnK7DMkTWAfLEopWOynqpOb_pcJyi0",
            serviceWorkerRegistration: serviceWorkerRegistration,
          });

          if (token) {
            console.log("Firebase Messaging Token:", token);
            await api.firebaseMessagingAddToken({
              token,
              userAgent: navigator.userAgent,
            });
            this.$toast("Ilmoitukset otettu käyttöön onnistuneesti.", {
              type: "success",
            });
          } else {
            console.log("No token received");
            throw Error("No token received");
          }
        } catch (err) {
          console.error("Notification permission error:", err);
          this.$toast("Virhe ilmoitusten käyttöönotossa.", {
            type: "error",
          });
        }
      })();
    },
    navigateToRoute(route, notification) {
      if (route) {
        // Redirect non-hallitus users from Hallitus tasks to general Tehtavat page
        if (
          !this.$isHallitus &&
          typeof route === "object" &&
          (route.name === "Hallitus" || route.path?.includes("hallitus")) &&
          (route.query?.tehtava || route.hash?.includes("tehtavat"))
        ) {
          this.$router.push({
            name: "Tehtävät",
            query: route.query,
          });
        } else {
          try {
            this.$router.push(route);
          } catch (e) {
            if (e.name !== "NavigationDuplicated") {
              console.error(e);
            }
          }
        }
      }
      if (notification) {
        this.markAsRead(notification);
      }
    },
    async markAsRead(notification) {
      if (!notification || notification.read) return;

      // Optimistic update
      notification.read = true;

      try {
        await api.markNotificationRead({ id: notification.id });
      } catch (e) {
        console.error(e);
        // Revert on error if needed, though rare
      }
    },
    async clearAllNotifications() {
      this.notifications = [];
      try {
        await api.clearNotifications();
      } catch (e) {
        console.error(e);
      }
    },
    formatDate(date) {
      return dayjs(date).format("DD.MM.YYYY HH:mm:ss");
    },
    hasHistory() {
      return window.history.length > 2;
    },
    reload() {
      if (window.swenv) {
        localStorage.setItem("swenv", window.swenv);
      }

      window.location.reload();
    },
    async reloadNotifications() {
      if (document.visibilityState === "hidden") return;
      this.loadingNotifications = true;
      try {
        this.notifications = orderBy(
          (await api.listNotifications())?.data,
          ["read", "createdAt"],
          ["asc", "desc"]
        );
      } catch (e) {
        console.error(e);
      }
      this.loadingNotifications = false;
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    toggleModernTheme() {
      this.$store.dispatch("toggleModernTheme");
    },
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.dark;
    },
    isDrawerDark() {
      return this.isDark || !this.modernTheme;
    },
    drawerColor() {
      if (!this.modernTheme) return "primary";
      return this.isDark ? "#1E1E1E" : undefined;
    },
    drawerSubheaderClass() {
      if (!this.modernTheme) return "";
      return this.isDark ? "grey darken-4" : "grey lighten-3";
    },
    drawerTitleClass() {
      if (this.isDark || !this.modernTheme) {
        return "white--text";
      }
      return "primary--text";
    },
    appBarColor() {
      if (this.isDark) {
        return this.modernTheme ? "#1E1E1E" : "primary";
      }
      return this.modernTheme ? "white" : "primary";
    },
    appBarContentColor() {
      if (this.isDark) {
        return "white";
      }
      return this.modernTheme ? "primary" : "white";
    },
    appBarTitleClass() {
      if (this.isDark) {
        return "white--text";
      }
      return this.modernTheme ? "primary--text" : "white--text";
    },
    totalPages() {
      return Math.ceil(this.notifications.length / this.pageSize) || 1;
    },
    modernTheme() {
      return this.$store.state.modernTheme;
    },
    paginatedNotifications() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      return this.notifications.slice(startIndex, startIndex + this.pageSize);
    },
    version() {
      return window.installedVersion;
    },
    latestVersion() {
      return window.latestVersion;
    },
    versionShort() {
      return (this.version || "").split("-")[0];
    },
    drawer() {
      return this.$store.state.drawer;
    },
    groupedRoutes() {
      const allRoutes = routes.filter((v) => v.nav !== false);
      const accessibleRoutes = allRoutes.filter(
        (v) => !v.roles || this.$hasRoleIn(v.roles)
      );

      const groups = {
        Yleinen: [],
        Hälytysosasto: [],
        Tukiosasto: [],
        Muu: [],
      };

      accessibleRoutes.forEach((route) => {
        if (route.category && groups[route.category]) {
          groups[route.category].push(route);
        } else {
          groups["Muu"].push(route);
        }
      });

      // Remove empty groups if needed, but template handles v-if="routes.length > 0"
      return groups;
    },
    routes() {
      const r = routes.filter((v) => v.nav !== false && !v.part);
      return r.filter((v) => !v.roles || this.$hasRoleIn(v.roles));
    },
    systemRoutes() {
      const r = routes.filter((v) => v.nav !== false && v.part === "system");
      return r.filter((v) => !v.roles || this.$hasRoleIn(v.roles));
    },
    unreadNotifications() {
      return this.notifications.filter((n) => !n.read).length;
    },
  },
};
</script>

<style lang="scss">
.safe-area-app-bar {
  padding-top: env(safe-area-inset-top) !important;
  height: calc(60px + env(safe-area-inset-top, 0px)) !important;
}

.safe-area-main {
  padding-top: calc(60px + env(safe-area-inset-top, 0px)) !important;
}

.nav-bar {
  background-color: #6c0000;
}

.mb-ios {
  margin-bottom: 90px;
}

.mb-normal {
  margin-bottom: 60px;
}

.refresh-list-item {
}
.notification-unread {
  background-color: rgba(255, 0, 0, 0.1);
}
</style>
